//client.ts — the browser's calls to our Worker

import type { Character } from '../../shared/character'
import type { CatalogSource } from '../../shared/customGarment'
import type { Garment } from '../../shared/garment'

export interface CatalogResponse {
	source: CatalogSource
	collection: string
	garments: Garment[]
	characters: Character[]
	skipped: string[]
	fetchedAt: string
	cached: boolean
}

export interface SessionResponse {
	apiKey: string
	expiresAt: string
	model: string
	maxSessionSeconds: number
}

export class ApiError extends Error {
	constructor(
		message: string,
		public readonly status: number,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

//Shared with the avatar app, which talks to its own Worker the same way.
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
	let response: Response
	try {
		response = await fetch(path, init)
	} catch (cause) {
		throw new ApiError(`Could not reach the service: ${(cause as Error).message}`, 0)
	}

	const body = await response.text()
	if (!response.ok) {
		throw new ApiError(extractError(body) ?? `Request failed with HTTP ${response.status}`, response.status)
	}
	try {
		return JSON.parse(body) as T
	} catch {
		throw new ApiError('The service returned a malformed response.', response.status)
	}
}

function extractError(body: string): string | null {
	try {
		const parsed = JSON.parse(body) as { error?: unknown }
		return typeof parsed.error === 'string' ? parsed.error : null
	} catch {
		return body.trim() || null
	}
}

export const fetchCatalog = (refresh = false): Promise<CatalogResponse> =>
	request<CatalogResponse>(`/api/catalog${refresh ? '?refresh=1' : ''}`)

//Moves whenever the admin portal saves; the mirror polls it to live-sync the catalogue.
export const fetchCatalogStamp = (): Promise<{ stamp: string }> =>
	request<{ stamp: string }>('/api/catalog-stamp')

//Shared by every mirror: switching here switches them all, which then follow via the stamp.
export const setCatalogSource = (source: CatalogSource): Promise<{ source: CatalogSource }> =>
	request<{ source: CatalogSource }>('/api/admin/source', {
		method: 'PUT',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ source }),
	})

//POSTs because it mints a credential — never GET, which would be prefetchable.
export const createSession = (model: string): Promise<SessionResponse> =>
	request<SessionResponse>('/api/session', {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ model }),
	})
